import React from 'react';
import { TrendingUp, Heart, Share2, CheckCheck, Eye } from 'lucide-react';

const InfluencerMetrics = ({ influencers = [] }) => {
  if (influencers?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2" />
            Métriques des influenceurs
          </h3>
        </div>
        <div className="text-center py-8">
          <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Aucun influenceur ne suit cet artiste</p>
        </div>
      </div>
    );
  }

  // Calculate aggregate metrics
  const totalEngagementScore = influencers?.reduce((sum, inf) => sum + (inf?.engagement_score || 0), 0) || 0;
  const averageEngagement = influencers?.length > 0 ? (totalEngagementScore / influencers?.length)?.toFixed(1) : 0;
  const totalContent = influencers?.reduce((sum, inf) => sum + (inf?.content_shared_count || 0), 0) || 0;
  const verifiedInfluencers = influencers?.filter(inf => inf?.is_verified)?.length || 0;

  const topInfluencers = [...influencers]
    ?.sort((a, b) => (b?.engagement_score || 0) - (a?.engagement_score || 0))
    ?.slice(0, 3) || [];

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2" />
          Métriques des influenceurs
        </h3>
        <span className="text-sm text-gray-500">{influencers?.length} influenceur{influencers?.length > 1 ? 's' : ''}</span>
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4">
          <div className="flex items-center">
            <TrendingUp className="w-5 h-5 text-blue-600 mr-2" />
            <div>
              <div className="text-xl font-bold text-blue-900">{averageEngagement}%</div>
              <div className="text-xs text-blue-600">Engagement moyen</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4">
          <div className="flex items-center">
            <Share2 className="w-5 h-5 text-purple-600 mr-2" />
            <div>
              <div className="text-xl font-bold text-purple-900">{totalContent}</div>
              <div className="text-xs text-purple-600">Contenus partagés</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCheck className="w-5 h-5 text-green-600 mr-2" />
            <div>
              <div className="text-xl font-bold text-green-900">{verifiedInfluencers}</div>
              <div className="text-xs text-green-600">Vérifiés</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4">
          <div className="flex items-center">
            <Heart className="w-5 h-5 text-orange-600 mr-2" />
            <div>
              <div className="text-xl font-bold text-orange-900">{influencers?.length}</div>
              <div className="text-xs text-orange-600">Followers</div>
            </div>
          </div>
        </div>
      </div>
      {/* Top Influencers */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-4">Top influenceurs</h4>
        <div className="space-y-3">
          {topInfluencers?.map((follow, index) => (
            <div key={follow?.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0 relative">
                <img
                  src={follow?.influencer?.user_profiles?.avatar_url || `/api/placeholder/40/40`}
                  alt={follow?.influencer?.user_profiles?.full_name}
                  className="w-10 h-10 rounded-full"
                />
                {follow?.is_verified && (
                  <CheckCheck className="absolute -bottom-0.5 -right-0.5 w-3 h-3 text-blue-500 bg-white rounded-full" />
                )}
                {index === 0 && (
                  <div className="absolute -top-1 -left-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-white">1</span>
                  </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {follow?.influencer?.user_profiles?.full_name}
                  </p>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center">
                      <TrendingUp className="w-3 h-3 text-green-500 mr-1" />
                      <span className="text-xs font-medium text-green-600">
                        {follow?.engagement_score || 0}%
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-gray-500 truncate">
                    {follow?.influencer?.business_name || 'Influenceur'}
                  </p>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <div className="flex items-center">
                      <Share2 className="w-3 h-3 mr-1" />
                      {follow?.content_shared_count || 0}
                    </div>
                    <div className="flex items-center">
                      <Eye className="w-3 h-3 mr-1" />
                      Suivi
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {influencers?.length > 3 && (
          <div className="mt-4 text-center">
            <button className="text-blue-600 hover:text-blue-500 text-sm font-medium">
              Voir tous les influenceurs ({influencers?.length})
            </button>
          </div>
        )}
      </div>
      {/* Growth Indicator */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Croissance des followers</span>
          <div className="flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600 font-medium">+12% ce mois</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfluencerMetrics;